// Rubricas: o catalogo de verbas de uma folha, e as duas fontes que o alimentam.
//
// A rubrica e' o ROTEADOR do dinheiro na folha — e' por ela que a prefeitura sabe
// pra quem mandar o repasse. A Atlas precisa dela para saber, de cada desconto:
// se e' consignacao, de quem e', e qual o produto (que decide o balde de margem).
//
// POR QUE UM CATALOGO E NAO UMA HEURISTICA. `bucketPorDescricao` foi testado
// contra as descricoes reais do I.M.A.M.N. e erra `SAQUE ANTECIPCARD`, que e'
// cartao beneficio e seria classificado como emprestimo — comeria os 35% em vez
// dos 5%, e o servidor passaria a poder contratar cartao sobre um teto cheio.
// O texto nao denuncia o produto e o cronograma tambem nao: aquele cartao
// amortiza em 60 parcelas fixas de valor constante, igual a um emprestimo.
// Produto e' DECLARADO. `bucketPorDescricao` fica como sugestao de tela.
//
// A CHAVE E' (FOLHA, VERBA), NAO (MUNICIPIO, VERBA). Morada Nova tem tres folhas
// — Prefeitura, A.M.T e I.M.A.M.N. — e a mesma consignataria tem numero
// diferente em cada uma: Banco do Brasil e' 285 na Prefeitura, 248 na A.M.T e
// 234 no I.M.A.M.N. Catalogo global estaria errado na segunda folha que entrasse.
//
// DUAS FONTES, mesma tabela de destino:
//
//   rubrica_funcionarios<AAAAM>.csv  (3a entrada do zip do retorno) — descobre as
//     verbas que tiveram movimento e se sao provento ou desconto. Nao diz credor
//     nem produto.
//   relatorio de conciliacao do ConsigFacil — DECLARA credor e produto por verba.
//     Quando existe, o catalogo e' derivado em vez de perguntado.
//
// A NATUREZA JA VEM RESPONDIDA. O `retorno-consignados` da competencia vem
// filtrado pela propria prefeitura: nas tres competencias do I.M.A.M.N. o
// holerite tem 6 descontos e o consignados traz sempre os mesmos 3 — IPREMN
// (previdencia), IRRF e SINDSEP nunca vazam. Estar no consignados e' a
// declaracao da prefeitura de que a verba e' consignacao. Prior forte, nao
// autoridade: export mal configurado criaria divida de imposto de renda, e e'
// pra isso que a conferencia humana existe (sao ~3 linhas por folha).
import { lerLinhasDelimitadas, parsearLinhaColunas } from "./csv-delimitado.js";
import { BUCKETS_MARGEM } from "./margem-bucket.js";
// ============================================================ holerite completo
/**
 * Nome da terceira entrada do zip, detectada por SUBSTRING — irma de
 * `ARQUIVO_CARGA` e `ARQUIVO_CONSIGNADOS` em retorno.ts.
 *
 * O ConsigFacil anexa a competencia ao nome e **sem zero no mes**:
 * `rubrica_funcionarios20264.csv` e' 202604, nao 20264. Exigir nome exato
 * recusaria o pacote real por um detalhe de nomenclatura.
 */
export const ARQUIVO_RUBRICAS = "rubrica";
/** Layout do `rubrica_funcionarios<AAAAM>.csv`. O arquivo real tem 11 campos; os
 *  5 do fim vem sempre vazios e a Atlas le os 6 primeiros. Note o valor em PONTO
 *  decimal (`1696.87`) — diferente do consignados, que usa virgula. */
export const COLUNAS_RUBRICA = [
    { nome: "competencia", coluna: 1, kind: "competencia", obrigatorio: true, descricao: "Competência, AAAAMM." },
    { nome: "matricula", coluna: 2, kind: "alfa", obrigatorio: true, descricao: "Matrícula do servidor. Casa com a coluna 2 do `arquivocarga`." },
    { nome: "codigoVerba", coluna: 3, kind: "alfa", obrigatorio: true, descricao: "Código da rubrica na folha. **Preserve os zeros à esquerda** — `0041` e `41` são a mesma verba, mas o arquivo traz a forma com zeros." },
    { nome: "descricao", coluna: 4, kind: "alfa", descricao: "Descrição da rubrica como sai no holerite. Sugere o credor; **não** determina o produto." },
    { nome: "tipo", coluna: 5, kind: "codigo", obrigatorio: true, codigos: ["P", "D"], descricao: "`P` provento · `D` desconto. Desconto **não** implica consignação: previdência e IRRF também são `D`." },
    { nome: "valor", coluna: 6, kind: "dinheiro", obrigatorio: true, descricao: "Valor da rubrica no holerite do mês." },
];
/** Le o holerite completo. Linha invalida e' recusada individualmente: um
 *  holerite de 215 linhas nao pode ser perdido por causa de uma. */
export function lerRubricaFuncionarios(texto) {
    const linhas = [];
    const erros = [];
    const celulasPorLinha = lerLinhasDelimitadas(texto);
    celulasPorLinha.forEach((celulas, i) => {
        const numero = i + 1;
        const { valores, erros: errosLinha } = parsearLinhaColunas(COLUNAS_RUBRICA, celulas);
        if (errosLinha.length > 0) {
            for (const e of errosLinha)
                erros.push({ linha: numero, ...e });
            return;
        }
        linhas.push({
            linha: numero,
            competencia: String(valores.competencia),
            matricula: String(valores.matricula),
            codigoVerba: String(valores.codigoVerba),
            descricao: String(valores.descricao ?? ""),
            tipo: valores.tipo,
            valor: Number(valores.valor),
        });
    });
    return { linhas, erros };
}
// ============================================================ codigo canonico
/**
 * Forma canonica do codigo da verba.
 *
 * O zip traz `0234` e o relatorio de conciliacao traz `234` — a mesma verba, e
 * sem normalizar as duas fontes nunca casam. Zero a esquerda so' cai em codigo
 * inteiramente numerico; codigo alfanumerico (`0A1`) fica como esta, porque ali
 * o zero pode ser significativo.
 */
export function normalizarCodigoVerba(bruto) {
    const t = bruto.trim().toUpperCase();
    if (!/^\d+$/.test(t))
        return t;
    const sem = t.replace(/^0+/, "");
    return sem === "" ? "0" : sem;
}
// ============================================================ descoberta
export const NATUREZA_VERBA = [
    "consignacao",
    "consignacao_nao_crediticia",
    "provento",
    "desconto_nao_consignado",
    "nao_classificada",
];
/**
 * Agrupa o holerite por verba e sugere a natureza de cada uma.
 *
 * `verbasDoConsignados` sao os codigos que o `retorno-consignados` da MESMA
 * competencia trouxe. Aceita as duas formas (`0234` ou `234`): normaliza dos
 * dois lados antes de comparar.
 */
export function descobrirVerbas(linhas, verbasDoConsignados) {
    const consignacao = new Set([...verbasDoConsignados].map(normalizarCodigoVerba));
    const porVerba = new Map();
    for (const l of linhas) {
        const canonico = normalizarCodigoVerba(l.codigoVerba);
        const existente = porVerba.get(canonico);
        if (existente) {
            existente.ocorrencias++;
            continue;
        }
        const noConsignados = consignacao.has(canonico);
        porVerba.set(canonico, {
            codigoVerba: l.codigoVerba,
            codigoCanonico: canonico,
            descricao: l.descricao,
            tipo: l.tipo,
            ocorrencias: 1,
            noConsignados,
            naturezaSugerida: noConsignados
                ? "consignacao"
                : l.tipo === "P"
                    ? "provento"
                    : "desconto_nao_consignado",
        });
    }
    return [...porVerba.values()];
}
// ============================================== relatorio de conciliacao
/**
 * Layout do relatorio de conciliacao do ConsigFacil. **Tem cabecalho**, ao
 * contrario dos arquivos do zip, e os campos vem entre aspas — exceto os tres de
 * valor.
 *
 * E' a fonte que DECLARA credor e produto por verba. Onde a prefeitura consegue
 * exportar isso, o catalogo e' derivado em vez de perguntado.
 */
export const COLUNAS_CONCILIACAO = [
    { nome: "folha", coluna: 1, kind: "alfa", obrigatorio: true, descricao: "Entidade pagadora. Um município tem mais de uma (prefeitura, autarquias, institutos), cada uma com numeração de verba própria." },
    { nome: "consignataria", coluna: 2, kind: "alfa", obrigatorio: true, descricao: "Quem recebe o repasse. **O credor, declarado** — não deduzido da descrição." },
    { nome: "matricula", coluna: 3, kind: "alfa", obrigatorio: true, descricao: "Matrícula do servidor." },
    { nome: "cpf", coluna: 4, kind: "alfa", descricao: "CPF formatado." },
    { nome: "nome", coluna: 5, kind: "alfa", descricao: "Nome do servidor." },
    { nome: "vinculo", coluna: 6, kind: "alfa", descricao: "Tipo de cargo/vínculo." },
    { nome: "contrato", coluna: 7, kind: "alfa", obrigatorio: true, descricao: "Identificador do contrato no ConsigFácil. **Único por dívida** — é a identidade que a coluna 7 do `retorno-consignados` deveria trazer e vem vazia." },
    { nome: "codigoInstituicao", coluna: 8, kind: "alfa", descricao: "Número do contrato no banco. Vazio para consignatária que não é banco." },
    { nome: "dataContrato", coluna: 9, kind: "alfa", descricao: "Data/hora da averbação, DD/MM/AAAA HH:MM." },
    { nome: "servico", coluna: 10, kind: "alfa", obrigatorio: true, descricao: "O **produto**, declarado: `Empréstimo Consignado`, `Cartão Benefício Saque`, `Mensalidade`." },
    { nome: "codigoVerba", coluna: 11, kind: "alfa", obrigatorio: true, descricao: "Código da rubrica **sem zeros à esquerda** — o zip traz a mesma verba com zeros." },
    { nome: "prazoOriginal", coluna: 12, kind: "num", descricao: "Total de parcelas. `0` em serviço recorrente (mensalidade)." },
    { nome: "prazo", coluna: 13, kind: "num", descricao: "Parcelas restantes, **incluindo a do mês**. Bate exatamente com a coluna 6 do `retorno-consignados`." },
    { nome: "valorOriginal", coluna: 14, kind: "dinheiro", descricao: "Parcela contratada." },
    { nome: "valorEnviado", coluna: 15, kind: "dinheiro", descricao: "Valor instruído à folha. `0,00` quando a dívida está bloqueada." },
    { nome: "valorDescontado", coluna: 16, kind: "dinheiro", descricao: "Valor efetivamente descontado. No dado real é **binário**: igual ao enviado ou zero, nunca parcial." },
    { nome: "criticaEnvio", coluna: 17, kind: "alfa", descricao: "Crítica do envio. Vazio em todo o dado observado." },
    { nome: "retorno", coluna: 18, kind: "alfa", obrigatorio: true, descricao: "`O valor enviado foi descontado` ou `Nao descontado`." },
    { nome: "periodoBloqueio", coluna: 19, kind: "alfa", descricao: "Desde quando está bloqueada, em texto (`Maio de 2026`)." },
    { nome: "motivo", coluna: 20, kind: "alfa", descricao: "Motivo, com o prefixo `Bloqueado` colado: `BloqueadoServidor sem margem inicial`, `BloqueadoProcesso de portabilidade`, `BloqueadoDeterminação Judicial`. `Nao informado` = não descontou e ninguém sabe por quê." },
    { nome: "ultimoPeriodoDescontado", coluna: 21, kind: "alfa", descricao: "Última competência em que o desconto saiu. Vazio = nunca descontou." },
    { nome: "processamento", coluna: 22, kind: "alfa", descricao: "Quando o relatório foi gerado." },
    { nome: "ajusteManual", coluna: 23, kind: "alfa", descricao: "`t`/`f`." },
    { nome: "dataAdmissao", coluna: 24, kind: "alfa", descricao: "Admissão, DD/MM/AAAA." },
    { nome: "eventos", coluna: 25, kind: "alfa", descricao: "JSON de eventos. `[]` em todo o dado observado." },
];
/** Prefixo que o ConsigFacil cola no motivo quando a divida esta travada. */
const PREFIXO_BLOQUEIO = "Bloqueado";
const RETORNO_DESCONTADO = "O valor enviado foi descontado";
/** O cabecalho e' reconhecido pelo nome da primeira coluna, nao pela posicao da
 *  linha: relatorio salvo de novo pode ganhar linha em branco antes. */
const CABECALHO_CONCILIACAO = /^\s*"?\s*folha\s*"?\s*;/i;
export function lerConciliacao(texto) {
    const linhas = [];
    const erros = [];
    lerLinhasDelimitadas(texto).forEach((celulas, i) => {
        const numero = i + 1;
        if (CABECALHO_CONCILIACAO.test(`"${celulas[0] ?? ""}";`))
            return;
        const { valores, erros: errosLinha } = parsearLinhaColunas(COLUNAS_CONCILIACAO, celulas);
        if (errosLinha.length > 0) {
            for (const e of errosLinha)
                erros.push({ linha: numero, ...e });
            return;
        }
        const motivoBruto = String(valores.motivo ?? "");
        const bloqueado = motivoBruto.startsWith(PREFIXO_BLOQUEIO);
        const retorno = String(valores.retorno ?? "");
        linhas.push({
            folha: String(valores.folha),
            consignataria: String(valores.consignataria),
            matricula: String(valores.matricula),
            contrato: String(valores.contrato),
            codigoInstituicao: String(valores.codigoInstituicao ?? ""),
            servico: String(valores.servico),
            codigoVerba: normalizarCodigoVerba(String(valores.codigoVerba)),
            prazoOriginal: Number(valores.prazoOriginal ?? 0),
            prazo: Number(valores.prazo ?? 0),
            valorOriginal: Number(valores.valorOriginal ?? 0),
            valorEnviado: Number(valores.valorEnviado ?? 0),
            valorDescontado: Number(valores.valorDescontado ?? 0),
            descontado: retorno === RETORNO_DESCONTADO,
            bloqueado,
            motivo: bloqueado ? motivoBruto.slice(PREFIXO_BLOQUEIO.length) : motivoBruto,
            ultimoPeriodoDescontado: String(valores.ultimoPeriodoDescontado ?? ""),
        });
    });
    return { linhas, erros };
}
// ============================================================ produto -> balde
/**
 * Servico declarado -> balde de margem.
 *
 * Devolve `null` no que nao tem balde, de proposito. `Mensalidade` (plano
 * odontologico) e' desconto facultativo, nao consignado: se consome os 35% ou e'
 * balde separado e' decisao de negocio aberta em 26/08/2026. Chutar
 * `EMPRESTIMO` faria o ODONTOPREV comer margem de emprestimo em silencio.
 *
 * Servico desconhecido tambem devolve `null`: verba sem balde nao gera divida, e
 * isso e' o comportamento seguro — so' pode restringir margem, nunca liberar.
 */
export function produtoDoServico(servico) {
    const t = servico
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .trim();
    // ORDEM: beneficio -> cartao -> emprestimo. Do mais especifico pro mais geral.
    //
    // `emprestimo` vinha PRIMEIRO e isso era um bug: "Cartao Consignado -
    // emprestimo sobre limite" (nome plausivel de produto, e o saque do cartao E'
    // um emprestimo sobre o limite) caia em EMPRESTIMO e comia os 35% em vez dos
    // 5% do cartao — exatamente o estrago que este catalogo existe pra evitar.
    //
    // Na outra direcao nao ha risco: "Emprestimo Consignado" nao contem "cartao".
    if (t.includes("benef"))
        return "CARTAO_BENEFICIOS";
    if (t.includes("cartao"))
        return "CARTAO_CONSIGNADO";
    if (t.includes("emprestimo"))
        return "EMPRESTIMO";
    return null;
}
/**
 * Rubrica do convenio para um produto.
 *
 * Cai no `codigoVerba` unico quando o produto nao tem entrada propria — e o
 * estado de TODO convenio existente, e tambem o meio da migracao (a prefeitura
 * declarou a rubrica do cartao e ainda nao a do beneficio). Devolver vazio faria
 * o contrato sair sem rubrica na remessa.
 */
export function verbaDoConvenio(conv, produto) {
    const doProduto = conv.codigosVerba?.[produto]?.trim();
    return doProduto && doProduto !== "" ? doProduto : conv.codigoVerba;
}
/**
 * Confere as rubricas declaradas nos convenios contra o catalogo da folha.
 *
 * A checagem que hoje NAO EXISTE, e a que mais dói: se o convenio com o Bradesco
 * aponta pra rubrica do Banco do Brasil, o desconto sai na rubrica do BB, o
 * repasse vai pro BB, e isso so aparece na conciliacao do mes seguinte.
 *
 * DIVERGENCIA E AVISO, NAO BLOQUEIO. A prefeitura pode ter trocado a rubrica e
 * nao avisado, e travar averbacao por isso seria pior que o problema. Por isso a
 * funcao devolve uma lista pra tela, e nao lanca.
 *
 * Verba fora do catalogo NAO e erro: o catalogo so conhece verba que movimentou.
 * Ela e listada como `verba_nao_catalogada`, que e a diferenca entre "esta
 * errado" e "ninguem verificou ainda".
 */
export function conferirConveniosContraCatalogo(convenios, rubricas) {
    const porVerba = new Map();
    for (const r of rubricas)
        porVerba.set(normalizarCodigoVerba(r.codigoVerba), r);
    const out = [];
    for (const conv of convenios) {
        // Uma entrada por produto declarado; `null` = o campo unico, que vale pros
        // tres produtos ao mesmo tempo.
        const declaradas = [];
        const mapa = conv.codigosVerba ?? {};
        for (const produto of BUCKETS_MARGEM) {
            const v = mapa[produto]?.trim();
            if (v)
                declaradas.push({ produto, verba: v });
        }
        if (declaradas.length === 0 && conv.codigoVerba.trim() !== "") {
            declaradas.push({ produto: null, verba: conv.codigoVerba.trim() });
        }
        // Mesma verba em dois produtos: e o defeito do `0267 = CONSIG. BRADESCO`
        // reproduzido do nosso lado. Rubrica somada nao permite conferir nenhum dos
        // tres tetos de margem, e o repasse por produto nao fecha na contabilidade
        // da prefeitura.
        const vistas = new Map();
        for (const d of declaradas) {
            if (d.produto === null)
                continue;
            const canonica = normalizarCodigoVerba(d.verba);
            const anterior = vistas.get(canonica);
            if (anterior) {
                out.push({
                    tipo: "verba_repetida_entre_produtos",
                    convenioId: conv.id, convenioNome: conv.nome,
                    codigoVerba: canonica, produtoDoConvenio: d.produto,
                    bancoDoConvenio: conv.bancoId,
                    mensagem: `a verba ${canonica} esta declarada para ${anterior} e ${d.produto} ao mesmo tempo: ` +
                        "o desconto chega somado e nenhum dos tetos de margem pode ser conferido",
                });
            }
            else {
                vistas.set(canonica, d.produto);
            }
        }
        for (const { produto, verba } of declaradas) {
            const canonica = normalizarCodigoVerba(verba);
            const rub = porVerba.get(canonica);
            // Sem rubrica, ou com rubrica ainda nao confirmada, nao ha o que comparar.
            if (!rub || rub.confirmadaEm === null) {
                out.push({
                    tipo: "verba_nao_catalogada",
                    convenioId: conv.id, convenioNome: conv.nome,
                    codigoVerba: canonica, produtoDoConvenio: produto,
                    bancoDoConvenio: conv.bancoId,
                    mensagem: rub
                        ? `a verba ${canonica} existe no catalogo mas ainda nao foi confirmada`
                        : `a verba ${canonica} nao esta no catalogo da folha: ninguem conferiu de quem ela e`,
                });
                continue;
            }
            if (rub.credorId !== null && rub.credorId !== conv.bancoId) {
                out.push({
                    tipo: "credor_diferente",
                    convenioId: conv.id, convenioNome: conv.nome,
                    codigoVerba: canonica, produtoDoConvenio: produto,
                    produtoDaRubrica: rub.tipoMargem,
                    bancoDoConvenio: conv.bancoId, credorDaRubrica: rub.credorId,
                    descricao: rub.descricao,
                    mensagem: `o convenio aponta a verba ${canonica} ("${rub.descricao}"), que no catalogo pertence a ` +
                        `outro credor: o desconto sairia na rubrica errada e o repasse iria pro banco errado`,
                });
                continue;
            }
            // Produto so e comparavel quando o convenio declarou um. O campo unico
            // vale pros tres, e cobrar produto dele seria acusar todo convenio antigo.
            if (produto !== null && rub.tipoMargem !== null && rub.tipoMargem !== produto) {
                out.push({
                    tipo: "produto_diferente",
                    convenioId: conv.id, convenioNome: conv.nome,
                    codigoVerba: canonica, produtoDoConvenio: produto,
                    produtoDaRubrica: rub.tipoMargem,
                    bancoDoConvenio: conv.bancoId, credorDaRubrica: rub.credorId,
                    descricao: rub.descricao,
                    mensagem: `o convenio declara a verba ${canonica} como ${produto} e o catalogo diz ${rub.tipoMargem}: ` +
                        "a parcela consumiria o balde de margem errado",
                });
            }
        }
    }
    return out;
}
/**
 * Deriva o catalogo do relatorio de conciliacao.
 *
 * `(folha, verba) -> (consignataria, servico)` foi funcao perfeita nas 2.623
 * linhas do relatorio real: 14 pares, nenhuma verba servindo dois credores ou
 * dois produtos. Estruturalmente tem que ser assim — se duas consignatarias
 * dividissem a rubrica, a prefeitura nao teria como dividir o repasse.
 *
 * Quando nao for, o par vai pra `conflitos` e **nao** gera entrada: catalogo
 * ambiguo nao classifica nada, porque escolher o credor errado faz um banco ver
 * contrato que nao e' dele e o repasse ir pro lugar errado.
 */
/** Separador de chave composta. Caractere de controle de proposito: nome de
 *  folha e de consignataria podem conter qualquer pontuacao visivel, e um
 *  separador que aparece no dado quebraria o agrupamento em silencio. */
const SEP = "";
export function derivarCatalogo(linhas) {
    const agrupado = new Map();
    for (const l of linhas) {
        const chave = `${l.folha}${SEP}${l.codigoVerba}`;
        const alvo = `${l.consignataria}${SEP}${l.servico}`;
        if (!agrupado.has(chave))
            agrupado.set(chave, new Map());
        const alternativas = agrupado.get(chave);
        const existente = alternativas.get(alvo);
        if (existente) {
            existente.contratos++;
            continue;
        }
        alternativas.set(alvo, {
            folha: l.folha,
            codigoVerba: l.codigoVerba,
            consignataria: l.consignataria,
            servico: l.servico,
            tipoMargem: produtoDoServico(l.servico),
            contratos: 1,
        });
    }
    const entradas = [];
    const conflitos = [];
    for (const alternativas of agrupado.values()) {
        const lista = [...alternativas.values()];
        if (lista.length === 1) {
            entradas.push(lista[0]);
            continue;
        }
        conflitos.push({
            folha: lista[0].folha,
            codigoVerba: lista[0].codigoVerba,
            alternativas: lista.map((e) => ({
                consignataria: e.consignataria,
                servico: e.servico,
                contratos: e.contratos,
            })),
        });
    }
    return { entradas, conflitos };
}
// ============================================ catalogo de verbas do banco
/**
 * LARGURA DA VERBA NO ARQUIVO DE DESCONTO.
 *
 * A coluna 10 do movimento tem largura 4 e e' escrita com `truncar: false` —
 * `textoEm` LANCA em vez de cortar, e o erro nao e' capturado em ponto nenhum
 * da API. Uma verba de 6 caracteres (`"PADRAO"`, que a criacao em lote gravava)
 * derrubava a geracao do arquivo INTEIRO: a unidade ficava sem remessa da
 * competencia por causa de um cadastro.
 *
 * Por isso o limite e' validado na ENTRADA, nos dois niveis — catalogo do banco
 * e declaracao do convenio. Ver `COLUNAS_MOVIMENTO` em movimento-layout.ts.
 */
export const MAX_CARACTERES_VERBA = 4;
/**
 * Confere um catalogo de banco inteiro antes de grava-lo.
 *
 * Devolve TODOS os problemas, nao o primeiro: a tela edita a lista inteira, e
 * mostrar um erro por vez faria o operador salvar tres vezes para descobrir
 * tres linhas erradas.
 */
export function validarCatalogoVerbas(itens) {
    const erros = [];
    const vistos = new Map();
    itens.forEach((item, indice) => {
        const bruto = (item.codigo ?? "").trim();
        const canonico = normalizarCodigoVerba(bruto);
        if (canonico === "") {
            erros.push({
                indice, campo: "codigo", codigo: bruto, motivo: "verba_vazia",
                mensagem: "codigo da verba vazio",
            });
            return;
        }
        if (bruto.length > MAX_CARACTERES_VERBA) {
            erros.push({
                indice, campo: "codigo", codigo: bruto, motivo: "verba_longa",
                mensagem: `a verba ${bruto} tem ${bruto.length} caracteres e o arquivo de desconto comporta ` +
                    `${MAX_CARACTERES_VERBA}: a geracao da remessa falharia para a folha inteira`,
            });
            return;
        }
        if ((item.descricao ?? "").trim() === "") {
            erros.push({
                indice, campo: "descricao", codigo: bruto, motivo: "descricao_vazia",
                mensagem: `a verba ${bruto} precisa de uma descricao`,
            });
        }
        // `0234` e `234` sao a MESMA verba. Comparar pelo texto cru deixaria as duas
        // entrarem, e o convenio que escolhesse uma delas apontaria para um vinculo
        // que a outra tambem reivindica.
        const anterior = vistos.get(canonico);
        if (anterior !== undefined) {
            erros.push({
                indice, campo: "codigo", codigo: bruto, motivo: "verba_duplicada",
                mensagem: `a verba ${bruto} ja esta na linha ${anterior + 1} do catalogo`,
            });
            return;
        }
        vistos.set(canonico, indice);
    });
    return erros;
}
/** As verbas que o cadastro de convenio oferece como candidatas. */
export function candidatasDoCatalogo(catalogo) {
    return (catalogo ?? []).filter((v) => v.situacao === "ativa");
}
/**
 * Confere as declaracoes de UM convenio antes de grava-las.
 *
 * Nao alcanca conflito com OUTROS convenios da mesma unidade — isso depende do
 * estado do sistema e fica na camada que tem acesso a ele.
 */
export function validarDeclaracoesDoConvenio(declaracoes) {
    const erros = [];
    const produtosVistos = new Map();
    const codigosVistos = new Map();
    declaracoes.forEach((d, indice) => {
        const bruto = (d.codigo ?? "").trim();
        const canonico = normalizarCodigoVerba(bruto);
        if (canonico === "") {
            erros.push({
                indice, produto: d.produto, codigo: bruto, motivo: "verba_vazia",
                mensagem: `a verba de ${d.produto} esta vazia`,
            });
            return;
        }
        if (bruto.length > MAX_CARACTERES_VERBA) {
            erros.push({
                indice, produto: d.produto, codigo: bruto, motivo: "verba_longa",
                mensagem: `a verba ${bruto} tem ${bruto.length} caracteres e o arquivo de desconto comporta ` +
                    `${MAX_CARACTERES_VERBA}: a geracao da remessa falharia para a folha inteira`,
            });
            return;
        }
        // UM PRODUTO POR DECLARACAO. Duas entradas do mesmo produto sao ambiguas:
        // nao ha como saber qual verba vale para ele.
        const produtoAnterior = produtosVistos.get(d.produto);
        if (produtoAnterior !== undefined) {
            erros.push({
                indice, produto: d.produto, codigo: bruto, motivo: "declaracao_multiproduto",
                mensagem: `${d.produto} ja foi declarado na linha ${produtoAnterior + 1}`,
            });
            return;
        }
        produtosVistos.set(d.produto, indice);
        // A MESMA VERBA EM DOIS PRODUTOS e' o defeito do `0267`: o desconto chega
        // somado e nenhum dos tres tetos pode ser conferido. O repasse por produto
        // tambem deixa de fechar na contabilidade da prefeitura.
        const produtoDoCodigo = codigosVistos.get(canonico);
        if (produtoDoCodigo !== undefined) {
            erros.push({
                indice, produto: d.produto, codigo: bruto, motivo: "verba_repetida_entre_produtos",
                mensagem: `a verba ${bruto} esta declarada para ${produtoDoCodigo} e ${d.produto} ao mesmo tempo: ` +
                    "o desconto chega somado e nenhum dos tetos de margem pode ser conferido",
            });
            return;
        }
        codigosVistos.set(canonico, d.produto);
    });
    return erros;
}
/**
 * O mapa que `verbaDoConvenio` consome, DERIVADO das declaracoes ativas.
 *
 * `codigosVerba` deixou de ser autoral na feature 004 e passou a sair daqui a
 * cada gravacao. Continua existindo porque a criacao de contrato o le, e mudar a
 * assinatura de `verbaDoConvenio` obrigaria a mexer neste pacote — que
 * `apps/api-nest` importa e o typecheck do CI alcanca.
 */
export function derivarCodigosVerba(declaracoes) {
    const mapa = {};
    for (const d of declaracoes) {
        if (d.situacao !== "ativa")
            continue;
        // O CODIGO COMO A FOLHA USA, com os zeros a' esquerda — nao o canonico.
        //
        // Este mapa vai parar no contrato e dali na COLUNA 10 do arquivo de
        // desconto, que tem largura 4 e e' preenchida com espacos a' direita: o
        // canonico `234` sai como `"234 "`, e a prefeitura le uma verba que nao
        // existe no cadastro dela. O canonico e' chave de comparacao, e toda
        // comparacao (`verbaAceitaPeloConvenio`, `conferirConveniosContraCatalogo`)
        // normaliza por conta propria — nenhuma delas depende de receber o mapa ja'
        // normalizado.
        const codigo = (d.codigoOriginal ?? d.codigo ?? "").trim();
        if (codigo !== "")
            mapa[d.produto] = codigo;
    }
    return mapa;
}
/** Produtos que o convenio oferta = produtos com verba ativa declarada. */
export function produtosOfertados(declaracoes) {
    return (declaracoes ?? []).filter((d) => d.situacao === "ativa").map((d) => d.produto);
}
/**
 * O convenio ainda depende da verba unica para algum produto?
 *
 * Convenio anterior a feature 004 tem `codigoVerba` e nenhuma declaracao: ele
 * continua operando pelo fallback e aparece sinalizado ate' alguem completar.
 */
export function convenioComVerbaIncompleta(conv) {
    const declarados = produtosOfertados(conv.verbas);
    if (declarados.length === 0)
        return (conv.codigoVerba ?? "").trim() !== "";
    return false;
}
/**
 * Este convenio oferta o produto ao servidor?
 *
 * E' a regra que decide QUAIS bancos aparecem quando o servidor pede um cartao
 * (ou um emprestimo) — a mesma nos dois clientes, web e app. Oferta quem
 * declarou verba ativa para o produto; e o convenio anterior a feature 004,
 * que so' tem a verba unica, oferta todos, porque ela atende todos enquanto
 * ninguem declarar por produto (FR-040).
 *
 * Sem esta regra o web mandava o pedido de cartao sem banco e deixava o
 * backend "decidir": com dois convenios na organizacao nao ha decisao segura,
 * e o pedido morria em "escolha a oferta de um banco" numa tela que nao tinha
 * como escolher (10/09/2026).
 */
export function convenioOfertaProduto(conv, produto) {
    if (produtosOfertados(conv.verbas).includes(produto))
        return true;
    return convenioComVerbaIncompleta(conv);
}
/**
 * As verbas que ESTE convenio pode pôr num contrato.
 *
 * Devolve os codigos como foram declarados (para a mensagem de recusa), sem
 * repetir a mesma verba em formas diferentes: `0234` e `234` sao a MESMA.
 *
 * Inclui a verba unica do convenio legado (FR-040): ela atende todos os
 * produtos enquanto ninguem declarar por produto, e recusa-la deixaria o
 * convenio antigo sem nenhuma verba valida.
 */
export function verbasAceitasDoConvenio(conv) {
    const vistas = new Set();
    const out = [];
    const acrescentar = (bruto) => {
        const codigo = (bruto ?? "").trim();
        if (codigo === "")
            return;
        const canonico = normalizarCodigoVerba(codigo);
        if (vistas.has(canonico))
            return;
        vistas.add(canonico);
        out.push(codigo);
    };
    for (const d of conv.verbas ?? []) {
        if (d.situacao === "ativa")
            acrescentar(d.codigo);
    }
    // O mapa derivado cobre o convenio gravado antes das declaracoes existirem.
    for (const produto of BUCKETS_MARGEM)
        acrescentar(conv.codigosVerba?.[produto]);
    acrescentar(conv.codigoVerba);
    return out;
}
/**
 * A verba pertence ao convenio?
 *
 * Existe porque `POST /v1/external/banco/contratos/alterar` aceitava QUALQUER
 * codigo e o gravava no contrato sem consultar nada. A verba e' o roteador do
 * repasse: um banco que mandasse a verba de outro credor redirecionaria o
 * dinheiro da parcela, e isso so' apareceria na conciliacao do mes seguinte.
 */
export function verbaAceitaPeloConvenio(conv, codigo) {
    const canonico = normalizarCodigoVerba((codigo ?? "").trim());
    if (canonico === "")
        return false;
    return verbasAceitasDoConvenio(conv).some((v) => normalizarCodigoVerba(v) === canonico);
}
//# sourceMappingURL=rubrica.js.map